import { reactive } from 'vue';
import { getLatestDraft, getLatestSaved } from '../../lib/callModContent';
import { useEventBus } from '@vueuse/core';
import { gzip, ungzip } from '../lib/gzip';
import { throttle } from 'lodash-es';

export const courseUpdates = useEventBus<CourseDraft>(Symbol('course-updates'));

/**
 * Courses, synced with localStorage
 */
export const courses = reactive<
  Record<string, { draft: CourseDraft; preview?: PreviewState }>
>({});

const getCourseKey = ({ courseId, firmCode }: CourseAndFirm) =>
  `course-${firmCode ? `${firmCode}_` : ''}${courseId}`;

// Load initial data from localStorage
for (const k in localStorage) {
  if (k.startsWith('course-')) {
    try {
      courses[k] = JSON.parse(
        ungzip(localStorage.getItem(k) || '') || 'undefined'
      );
    } catch (e) {
      console.error('Error loading course from local storage', k, e);
    }
  }
}

/**
 * Updates a course in the store and localStorage
 */
export const setCourse = (draft: CourseDraft) => {
  courseUpdates.emit(draft);
};

const storageOrder: string[] =
  JSON.parse(localStorage.getItem('course-order') || '[]') || [];

const maxStorage = 30;

const backupDraft = (draft: CourseDraft) => {
  const k = getCourseKey(draft);
  courses[k] = { ...courses[k], draft };

  try {
    if (storageOrder.includes(k)) {
      storageOrder.splice(storageOrder.indexOf(k), 1);
    }

    storageOrder.push(k);

    if (storageOrder.length > maxStorage) {
      localStorage.removeItem(storageOrder.shift() || '');
    }

    const str = JSON.stringify(courses[k]);

    localStorage.setItem(k, gzip(str));
    localStorage.setItem('course-order', JSON.stringify(storageOrder));
  } catch (e) {
    console.error('Error setting course in local storage', k, e);
  }
};

const throttleBackupDraft = throttle(backupDraft, 3000, {
  leading: true,
  trailing: true
});

courseUpdates.on(throttleBackupDraft);

// @ts-ignore
window.recoverDraft = (name: string) => {
  const k = `course-${name}`;
  const str = localStorage.getItem(k);
  if (!str) return;

  console.log(JSON.parse(ungzip(str)).draft);
};

/**
 * Clears local storage when exiting the editor so we load it from the DB. But while the editor is open, local storage persists the course in case of a refresh with poor connection.
 */
export const clearCourse = (course: CourseAndFirm) => {
  const k = getCourseKey(course);
  delete courses[k];
  localStorage.removeItem(k);
};

export const loadCourse = async (c: CourseAndFirm) => {
  const [latestDraft, latestSaved] = await Promise.all([
    getLatestDraft(c),
    getLatestSaved(c)
  ]);

  const latest =
    latestDraft && latestSaved
      ? // if both exist, use newer; tie goes to the draft (uncommitted work)
        latestDraft?.metadata.updatedDate >= latestSaved?.metadata.createdDate
        ? latestDraft
        : latestSaved
      : latestDraft || latestSaved;

  const hasUnsavedChanges =
    latestDraft &&
    (latestSaved
      ? latestDraft?.metadata.updatedDate >= latestSaved?.metadata.createdDate
      : true);

  if (!latest) return;

  // save and return it
  setCourse(latest);
  return { latest, latestDraft, latestSaved, hasUnsavedChanges };
};

export const getCourse = (c: CourseAndFirm) => courses[getCourseKey(c)];

export const setScroll = (c: CourseAndFirm, scroll: number) => {
  const k = getCourseKey(c);
  courses[k].preview = courses[k].preview ?? {};
  courses[k].preview.scroll = scroll;
};

export const setLocation = (c: CourseAndFirm, location: string) => {
  const k = getCourseKey(c);
  courses[k].preview = courses[k].preview ?? {};
  courses[k].preview.location = location;
};
